// Monthly MDCF (Monthly Data Capture Format) — data shape, defaults, localStorage.
import { buildDayBreakdown } from "./mealPlan";
import { isHolidayDay, isNoMdmDay, reasonLabel } from "./dayKind";
import { dayAmounts, CONS_CLASSES } from "./consumption";

const STORE_PREFIX = "pm-poshan-mdcf-v1";

const MONTH_OPTIONS = [
  { v: 1,  odia: "ଜାନୁଆରୀ",   en: "January" },
  { v: 2,  odia: "ଫେବୃଆରୀ",   en: "February" },
  { v: 3,  odia: "ମାର୍ଚ୍ଚ",    en: "March" },
  { v: 4,  odia: "ଏପ୍ରିଲ",     en: "April" },
  { v: 5,  odia: "ମେ",         en: "May" },
  { v: 6,  odia: "ଜୁନ",        en: "June" },
  { v: 7,  odia: "ଜୁଲାଇ",      en: "July" },
  { v: 8,  odia: "ଅଗଷ୍ଟ",      en: "August" },
  { v: 9,  odia: "ସେପ୍ଟେମ୍ବର", en: "September" },
  { v: 10, odia: "ଅକ୍ଟୋବର",    en: "October" },
  { v: 11, odia: "ନଭେମ୍ବର",    en: "November" },
  { v: 12, odia: "ଡିସେମ୍ବର",   en: "December" },
];

export { MONTH_OPTIONS };

function emptyCatRow() {
  return { balBatika: "", primaryClass: "", upperPrimaryClass: "", upperSchool: "" };
}
function emptyFinanceRow() {
  return { previousBalance: "", received: "", expense: "", remaining: "" };
}
function emptyFinRows() {
  return {
    balBatika: emptyFinanceRow(), primaryClass: emptyFinanceRow(),
    upperPrimaryClass: emptyFinanceRow(), upperSchool: emptyFinanceRow(),
  };
}
function emptyEngFundRow() {
  return { openingBalance: "", receivedDuringMonth: "", expenditureDuringMonth: "", closingBalance: "" };
}
function emptyGrainRow() {
  return { openingBalance: "", receivedDuringMonth: "", consumptionDuringMonth: "", closingBalance: "" };
}
function emptyMealsRow() {
  return { balVatika: "", primary: "", upperPrimary: "" };
}

export function defaultGroceries() {
  return [
    "ଡାଲି (Dal)", "ସୋୟାବତି (Soya)", "ଅଣ୍ଡା (Eggs)", "ପରିବା (Vegetables)", "ତେଲ (Oil)",
    "ମସଲା ଓ ଲୁଣ (Spices & Salt)", "ଜାଳେଣୀ (Fuel)", "ମାଣ୍ଡିଆ ଚୁନା (Ragi Flour)",
    "ଗହମ ଚୁନା (Wheat Flour)", "ଗୁଡ଼ (Jaggery)", "ରାଶି (Sesame)", "ଚିନାବାଦାମ (Peanuts)",
    "ଗୁଜୁରାତି ଗୁଣ୍ଡ (Cardamom)", "ଲଡୁ ପ୍ରସ୍ତୁତି (Ladoo Making)",
  ].map((name) => ({
    name,
    balBatika_qty: "", balBatika_price: "",
    primary_qty: "", primary_price: "",
    upperPrimary_qty: "", upperPrimary_price: "",
    upperSchool_qty: "", upperSchool_price: "",
  }));
}

export function defaultMdcfData() {
  return {
    general: {
      schoolSerialNo: "", schoolName: "", month: "", year: "", totalChildren: "", udiseCode: "",
      schoolType: "Government", category: "Primary with Upper Primary",
      stateUT: "Odisha", district: "", blockNP: "", villageWard: "",
      kitchenType: "", ngoShg: "", enrolment: "",
    },
    monthly: {
      section2: {
        totalStudents: emptyCatRow(),
        totalWorkingDays: emptyCatRow(),
        mdmServedDays: emptyCatRow(),
        totalFoodExpense: emptyCatRow(),
      },
      section6: emptyFinRows(),
      section7: emptyFinRows(),
      bankAccounts: [
        { slNo: "1", committeeName: "", gender: "", category: "", bankAccountNo: "", ifscBranchName: "", amount: "" },
      ],
      section9: { previousBalance: "", received: "", expense: "", remaining: "" },
      groceries: defaultGroceries(),
      section10TotalMeals: { balBatika: 0, primaryClass: 0, upperPrimaryClass: 0, upperSchool: 0 },
      section11: { committeeMember: "", blockEducationOfficer: "", otherMember: "" },
    },
    eng: {
      mealsAvailed: {
        enrolment: emptyMealsRow(),
        schoolDays: emptyMealsRow(),
        mdmDaysServed: emptyMealsRow(),
        totalMealsServed: emptyMealsRow(),
      },
      fundDetails: {
        cookingCostBalVatika: emptyEngFundRow(),
        cookingCostPrimary: emptyEngFundRow(),
        cookingCostUpperPrimary: emptyEngFundRow(),
        cookCumHelper: emptyEngFundRow(),
        mmeExpenses: emptyEngFundRow(),
        closingMatchesBank: "",
      },
      cookDetails: [
        { slNo: "1", cookName: "", gender: "", category: "", paymentMode: "Bank", amountReceived: "" },
      ],
      grainDetails: {
        balVatikaWheat: emptyGrainRow(), balVatikaRice: emptyGrainRow(),
        primaryWheat: emptyGrainRow(), primaryRice: emptyGrainRow(),
        upperPrimaryWheat: emptyGrainRow(), upperPrimaryRice: emptyGrainRow(),
      },
      healthStatus: { ifaBoys: "", ifaGirls: "", rbskScreened: "", rbskReferred: "" },
      inspection: {
        inspectionDone: "",
        byTaskForce: "", byDistrictOfficials: "",
        byBlockOfficials: "", bySMCMembers: "", untowardIncidents: "",
      },
    },
  };
}

function storeKey(schoolId, year, month) {
  return `${STORE_PREFIX}:${schoolId || "no-school"}:${year}:${String(month).padStart(2, "0")}`;
}

// Fill keys missing from an older / sparse saved MDCF with the template defaults.
function mergeTemplate(tpl, saved) {
  if (!saved || typeof saved !== "object" || Array.isArray(saved)) return saved ?? tpl;
  const out = { ...saved };
  Object.keys(tpl || {}).forEach((k) => {
    const t = tpl[k];
    if (out[k] === undefined || out[k] === null) out[k] = t;
    else if (t && typeof t === "object" && !Array.isArray(t)) out[k] = mergeTemplate(t, out[k]);
  });
  return out;
}

export function loadMdcf(schoolId, year, month) {
  try {
    const raw = window.localStorage.getItem(storeKey(schoolId, year, month));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveMdcf(schoolId, year, month, data) {
  try {
    window.localStorage.setItem(storeKey(schoolId, year, month), JSON.stringify(data));
  } catch (err) {
    console.error("mdcf save failed", err);
  }
}

// ---------- Auto-fill from Active School + Daily Entries ----------

const toN = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Map our school-profile schoolCategory shorthand (e.g. "BV+Primary",
 * "BV+Primary+UP", "Primary+UP+HS") to the PM POSHAN MDCF Category enum
 * ("Primary", "Upper Primary", "Primary with Upper Primary"). Used to
 * auto-populate the "Category (English MDCF)" Quick-Edit field.
 *
 * PM POSHAN doesn't have a "Bal Vatika only" or "High School only" bucket;
 * BV is treated as Primary, HS is treated as Upper Primary for category
 * purposes. The classwise data still shows real BV / HS rows separately.
 */
export function schoolCategoryToMdcfCategory(cat) {
  if (!cat || typeof cat !== "string") return "";
  const k = cat.replace(/\s+/g, "").toUpperCase();
  const hasBV   = /BV|BALVATIKA|BALBATIKA/.test(k);
  const hasPri  = /PRIMARY(?!\+UP|\+HS|WITH)|^PRIMARY|^PRI\b|\+PRIMARY|\+PRI\b|^PRI$|PRIMARY$/.test(k) || k.includes("PRIMARY");
  const hasUP   = /UPPERPRIMARY|UP\b|\+UP|\+UPPER/.test(k);
  const hasHS   = /HIGHSCHOOL|HS\b|\+HS|\+HIGH/.test(k);
  // BV alone or BV+Primary → "Primary"
  // Anything with UP or HS → "Primary with Upper Primary"
  if (hasUP || hasHS) return "Primary with Upper Primary";
  if (hasBV || hasPri) return "Primary";
  return "";
}

/**
 * Build auto-filled MDCF data from active school profile, array of daily entries
 * (already filtered to the target month), the selected month (1..12) & year,
 * a working-days count (from month-lock config if available), and a cook
 * payments array (optional — from the Quarterly Reports months array or
 * a standalone cookDetails list).
 */
export function autoFillMdcf({ base, school, dailyEntries, monthlyCooks, cchs, fundBalance, prevFundBalance, prevConsumed, year, month }) {
  const data = base ? mergeTemplate(defaultMdcfData(), JSON.parse(JSON.stringify(base))) : defaultMdcfData();
  const monthDef = MONTH_OPTIONS.find((m) => m.v === Number(month));
  const monthLabel = monthDef ? `${monthDef.odia} / ${monthDef.en}` : "";
  const yrStr = String(year || "");

  // General / school-identity autofill — ALWAYS overwrite from school
  // profile so the MDCF stays in sync when the user updates school details.
  // iter113 — also auto-set:
  //   • schoolSerialNo  ← school.udiseCode (PM POSHAN convention)
  //   • totalChildren   ← sum of class-wise enrolment
  //   • enrolment       ← same sum
  //   • category        ← mapped from school.schoolCategory
  //   • stateUT         ← "Odisha" (kept from default)
  if (school) {
    data.general.schoolName = school.schoolName || data.general.schoolName;
    data.general.udiseCode = school.udiseCode || data.general.udiseCode;
    // School Serial No. = UDISE code per PM POSHAN bill format.
    data.general.schoolSerialNo = school.udiseCode || data.general.schoolSerialNo || "";
    data.general.district = school.district || data.general.district;
    data.general.blockNP = school.block || data.general.blockNP;
    data.general.villageWard = school.village || data.general.villageWard;
    data.general.ngoShg = school.ngoShg || data.general.ngoShg || "";
    // Category — map from school's shorthand (BV+Primary etc.) to PM POSHAN enum.
    const mappedCategory = schoolCategoryToMdcfCategory(school.schoolCategory);
    if (mappedCategory) {
      data.general.category = mappedCategory;
    }
    // State / UT defaults to "Odisha" — explicit assignment so legacy docs
    // also get it on re-autofill.
    if (!data.general.stateUT) data.general.stateUT = "Odisha";
    // Classwise enrolment (used by reports to render "--" for non-enrolled classes)
    data.general.enrolmentByClass = {
      balBatika: toN(school.balVatikaEnrollment),
      primaryClass: toN(school.primaryEnrollment),
      upperPrimaryClass: toN(school.upperPrimaryEnrollment),
      upperSchool: toN(school.highSchoolEnrollment),
    };
    const enrolmentTotal =
      toN(school.balVatikaEnrollment) + toN(school.primaryEnrollment) +
      toN(school.upperPrimaryEnrollment) + toN(school.highSchoolEnrollment);
    if (enrolmentTotal > 0) {
      data.general.enrolment = String(enrolmentTotal);
      data.general.totalChildren = String(enrolmentTotal);
    }
  }
  if (monthLabel) data.general.month = monthLabel;
  if (yrStr) data.general.year = yrStr;

  // Aggregate daily entries — track MDM vs Dry Ration separately so the rendered
  // cells can be formatted as "{mdm}+{dr}={total}".
  const att = { balBatika: 0, primaryClass: 0, upperPrimaryClass: 0, upperSchool: 0 };
  const mealsAvailed = { balVatika: 0, primary: 0, upperPrimary: 0 };
  // mdm vs dr buckets
  const mdmFundsByClass = { balBatika: 0, primaryClass: 0, upperPrimaryClass: 0, upperSchool: 0 };
  const drFundsByClass = { balBatika: 0, primaryClass: 0, upperPrimaryClass: 0, upperSchool: 0 };
  const mdmRiceByClass = { balBatika: 0, primaryClass: 0, upperPrimaryClass: 0, upperSchool: 0 };
  const drRiceByClass = { balBatika: 0, primaryClass: 0, upperPrimaryClass: 0, upperSchool: 0 };
  const mdmMeals = { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 };
  const drMeals = { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 };
  // groceriesByClass: ingredient name → { balBatika: {qty, amount}, primaryClass:..., upperPrimaryClass:..., upperSchool:... }
  const groceriesByClass = new Map();
  const mdmDaysSet = new Set();
  const drDaysSet = new Set();

  const noMdmDaysSet = new Set();
  const noMdmByDate = {};

  const PER_CLASS_KEY = ["balBatika", "primaryClass", "upperPrimaryClass", "upperSchool"];

  (dailyEntries || []).forEach((e) => {
    // iter154 — Working-day-no-MDM entries count toward the working-day
    // total but produce no meals / rice / groceries. We record them in
    // their own set so Section 2 can show `mdm + dr + no_mdm = total`
    // and the remarks line can list the dates.
    if (e.dayKind === "working_no_mdm") {
      noMdmDaysSet.add(e.date);
      noMdmByDate[e.date] = e.noMdmReason || "other";
      return;
    }
    if (e.dayKind === "holiday") return;
    const a = e.attendance || {};
    const isDR = e.entryType === "dryration";
    const bv = toN(a.balVatika), pr = toN(a.primary), up = toN(a.upperPrimary), hs = toN(a.highSchool);
    const counts = [bv, pr, up, hs];
    att.balBatika += bv; att.primaryClass += pr; att.upperPrimaryClass += up; att.upperSchool += hs;
    mealsAvailed.balVatika += bv; mealsAvailed.primary += pr; mealsAvailed.upperPrimary += up;
    if (bv + pr + up + hs > 0) {
      if (isDR) drDaysSet.add(e.date); else mdmDaysSet.add(e.date);
    }
    if (isDR) {
      drMeals.balVatika += bv; drMeals.primary += pr; drMeals.upperPrimary += up; drMeals.highSchool += hs;
    } else {
      mdmMeals.balVatika += bv; mdmMeals.primary += pr; mdmMeals.upperPrimary += up; mdmMeals.highSchool += hs;
    }

    // Rs / kg per class from the shared helper (same numbers as Fund Management).
    const amt = dayAmounts(e);
    if (amt) {
      const fundTarget = isDR ? drFundsByClass : mdmFundsByClass;
      const riceTarget = isDR ? drRiceByClass : mdmRiceByClass;
      PER_CLASS_KEY.forEach((key, idx) => {
        fundTarget[key] += amt.cost[CONS_CLASSES[idx]];
        riceTarget[key] += amt.rice[CONS_CLASSES[idx]];
      });
    }
    const bk = buildDayBreakdown(e.date, a);
    if (bk && bk.perClass) {
      // Groceries — Section 10 must show ONLY Mid-Day-Meal (MDM) consumption.
      // Dry Ration distributions are tracked separately in fund/rice sections;
      // including them here would double-count ingredient quantities the kitchen
      // never actually cooked.
      if (!isDR) {
        bk.perClass.forEach((cls, idx) => {
        const headcount = counts[idx];
        if (!headcount || !cls.lines) return;
        cls.lines.forEach((line) => {
          if (line.item === "Rice") return;
          const bucket = groceriesByClass.get(line.item) || {
            balBatika: { qty: 0, amount: 0 }, primaryClass: { qty: 0, amount: 0 },
            upperPrimaryClass: { qty: 0, amount: 0 }, upperSchool: { qty: 0, amount: 0 },
            unit: line.unit,
          };
          const key = PER_CLASS_KEY[idx];
          // For flat-cost items (Masala & Salt, Fuel) we now have a real
          // `qtyPerStudent` from the meal plan. Use it so MDCF Section 10
          // (ଚାଉଳ ବ୍ୟତୀତ ଅନ୍ୟାନ୍ୟ ସାମଗ୍ରୀ) shows a non-zero quantity per
          // class in the qty column alongside the existing amount.
          const qPerStudent = line.costMode === "flat"
            ? Number(line.qtyPerStudent || 0)
            : Number(line.qty || 0);
          bucket[key].qty += qPerStudent * headcount;
          bucket[key].amount += (line.costPerStudent || 0) * headcount;
          groceriesByClass.set(line.item, bucket);
        });
      });
      }  // end if (!isDR) — Section 10 is MDM-only.
    }
  });

  // Combined totals (used where formula format is not required)
  const fundsByClass = {
    balBatika: mdmFundsByClass.balBatika + drFundsByClass.balBatika,
    primaryClass: mdmFundsByClass.primaryClass + drFundsByClass.primaryClass,
    upperPrimaryClass: mdmFundsByClass.upperPrimaryClass + drFundsByClass.upperPrimaryClass,
    upperSchool: mdmFundsByClass.upperSchool + drFundsByClass.upperSchool,
  };
  const riceByClass = {
    balBatika: mdmRiceByClass.balBatika + drRiceByClass.balBatika,
    primaryClass: mdmRiceByClass.primaryClass + drRiceByClass.primaryClass,
    upperPrimaryClass: mdmRiceByClass.upperPrimaryClass + drRiceByClass.upperPrimaryClass,
    upperSchool: mdmRiceByClass.upperSchool + drRiceByClass.upperSchool,
  };
  const daysServedSet = new Set([...mdmDaysSet, ...drDaysSet]);

  // Helper — render "{mdm}+{dr}={total}" or just "{total}" when no DR data.
  // iter154 — now accepts an optional 3rd "no-mdm days" count. When
  // present and non-zero, output becomes "{mdm}+{dr}+{noMdm}={total}".
  // Legacy callers (noMdm omitted or 0) keep the 2-part / 1-part shape
  // so old entries render byte-identically to before.
  const fmt = (m, d, noMdm = 0, decimals = 2) => {
    const total = m + d + noMdm;
    if (total === 0) return "";
    const round = (x) => (decimals === 0 ? String(Math.round(x)) : x.toFixed(decimals));
    const parts = [];
    if (m > 0 || d > 0 || noMdm > 0) {
      if (m > 0) parts.push(round(m));
      if (d > 0) parts.push(round(d));
      if (noMdm > 0) parts.push(round(noMdm));
    }
    if (parts.length <= 1) return round(total);
    return `${parts.join("+")}=${round(total)}`;
  };

  // Section 3 — working days = mdm days + dry-ration days (formula format)
  // iter154 — totalWorkingDays now includes No-MDM days as a 3rd part
  // so admin dashboards & MDCF never disagree (same helpers). The
  // existing fmt(a,b,c) formatter prints "a+b+c=sum" when c>0; when
  // c===0 it emits the legacy 2-part form, so old entries render
  // byte-identically to before.
  const workingDaysFmt = fmt(mdmDaysSet.size, drDaysSet.size, noMdmDaysSet.size, 0, 0);
  data.monthly.section2.totalWorkingDays.balBatika = workingDaysFmt;
  data.monthly.section2.totalWorkingDays.primaryClass = workingDaysFmt;
  data.monthly.section2.totalWorkingDays.upperPrimaryClass = workingDaysFmt;
  data.monthly.section2.totalWorkingDays.upperSchool = workingDaysFmt;
  // Section 4 — MDM days served (formula: mdm + dr = total)
  const servedDaysFmt = fmt(mdmDaysSet.size, drDaysSet.size, 0, 0);
  data.monthly.section2.mdmServedDays.balBatika = servedDaysFmt;
  data.monthly.section2.mdmServedDays.primaryClass = servedDaysFmt;
  data.monthly.section2.mdmServedDays.upperPrimaryClass = servedDaysFmt;
  data.monthly.section2.mdmServedDays.upperSchool = servedDaysFmt;
  // Section 5 (ମାସରେ ପରିବେଷଣ କରାଯାଇଥିବା ମୋଟ ଖାଦ୍ୟ) = total meals served per class as MDM+DR=Total
  const mdmAtt = { balBatika: 0, primaryClass: 0, upperPrimaryClass: 0, upperSchool: 0 };
  const drAtt = { balBatika: 0, primaryClass: 0, upperPrimaryClass: 0, upperSchool: 0 };
  (dailyEntries || []).forEach((e) => {
    const a = e.attendance || {};
    const target = e.entryType === "dryration" ? drAtt : mdmAtt;
    target.balBatika += toN(a.balVatika);
    target.primaryClass += toN(a.primary);
    target.upperPrimaryClass += toN(a.upperPrimary);
    target.upperSchool += toN(a.highSchool);
  });
  data.monthly.section2.totalFoodExpense.balBatika = fmt(mdmAtt.balBatika, drAtt.balBatika, 0, 0);
  data.monthly.section2.totalFoodExpense.primaryClass = fmt(mdmAtt.primaryClass, drAtt.primaryClass, 0, 0);
  data.monthly.section2.totalFoodExpense.upperPrimaryClass = fmt(mdmAtt.upperPrimaryClass, drAtt.upperPrimaryClass, 0, 0);
  data.monthly.section2.totalFoodExpense.upperSchool = fmt(mdmAtt.upperSchool, drAtt.upperSchool, 0, 0);
  if (school) {
    data.monthly.section2.totalStudents.balBatika = String(toN(school.balVatikaEnrollment) || "");
    data.monthly.section2.totalStudents.primaryClass = String(toN(school.primaryEnrollment) || "");
    data.monthly.section2.totalStudents.upperPrimaryClass = String(toN(school.upperPrimaryEnrollment) || "");
    data.monthly.section2.totalStudents.upperSchool = String(toN(school.highSchoolEnrollment) || "");
  }

  // Section 6 — expense classwise (MDM+DR=Total)
  ["balBatika", "primaryClass", "upperPrimaryClass", "upperSchool"].forEach((k) => {
    data.monthly.section6[k].expense = fmt(mdmFundsByClass[k], drFundsByClass[k], 0, 2);
  });

  // Section 7 — rice consumption classwise (kg) MDM+DR=Total (3-decimal)
  ["balBatika", "primaryClass", "upperPrimaryClass", "upperSchool"].forEach((k) => {
    data.monthly.section7[k].expense = fmt(mdmRiceByClass[k], drRiceByClass[k], 0, 3);
  });

  // Section 8 — bank accounts auto-fill from CCH master records.
  // Use Odia name when available so the printed Odia bill matches the form.
  // Amount auto-fetched from CCH `monthlyHonorarium` (₹). Empty when 0/missing.
  if (Array.isArray(cchs) && cchs.length > 0) {
    data.monthly.bankAccounts = cchs.map((c, i) => {
      const hon = Number(c.monthlyHonorarium) || 0;
      return {
        slNo: String(i + 1),
        committeeName: c.nameOdia || c.name || "",
        gender: c.gender || "",
        category: c.category || "",
        bankAccountNo: c.bankAccount || "",
        ifscBranchName: c.ifsc || c.ifscBranchName || "",
        amount: hon > 0 ? hon.toFixed(2) : "",
      };
    });
  }

  // Section 10 — Groceries: render a fixed list of 15 ingredient rows in a
  // pre-defined Odia order. Each row is always present, even if no daily
  // entries used that ingredient — empty cells stay blank/`--` per existing
  // class-active rules.
  const SECTION_10_ROWS = [
    { odia: "ଡାଲି",                              eng: "Dal" },
    { odia: "ସୋୟାବଡି",                          eng: "Soya" },
    { odia: "ଅଣ୍ଡା",                              eng: "Egg" },
    { odia: "ପରିବା",                              eng: "Vegetable" },
    { odia: "ତେଲ",                                eng: "Oil" },
    { odia: "ମସଲା ଓ ଲୁଣ",                      eng: "Masala & Salt" },
    { odia: "ଜାଳେଣି",                            eng: "Fuel" },
    { odia: "ମାଣ୍ଡିଆ ଚୂନା",                    eng: "Ragi Flour" },
    { odia: "ଗହମ ଚୂନା",                         eng: "Wheat Flour" },
    { odia: "ଗୁଡ",                                eng: "Sugar" },
    { odia: "ରାଶି",                              eng: "Sesame" },
    { odia: "ଚିନାବାଦାମ",                       eng: "Ground Nuts" },
    { odia: "ଘିଅ",                                eng: "Refined Oil" },
    { odia: "ଗୁଜୁରାତି ଗୁଣ୍ଡ",                 eng: "Cardamom" },
    { odia: "ଲଡୁ ପ୍ରସ୍ତୁତି ଖର୍ଚ୍ଚ (ରୋଷେୟା ପାଇବେ)", eng: "Ladoo Making Cost" },
  ];
  const groceryRows = SECTION_10_ROWS.map(({ odia, eng }) => {
    const cls = groceriesByClass.get(eng);
    const row = { name: odia, unit: cls?.unit || "" };
    const fillClass = (key, prefix) => {
      const c = cls?.[key];
      row[`${prefix}_qty`]   = c && c.qty > 0    ? Number(c.qty.toFixed(3))    : "";
      row[`${prefix}_price`] = c && c.amount > 0 ? Number(c.amount.toFixed(2)) : "";
    };
    fillClass("balBatika",         "balBatika");
    fillClass("primaryClass",      "primary");
    fillClass("upperPrimaryClass", "upperPrimary");
    fillClass("upperSchool",       "upperSchool");
    return row;
  });
  data.monthly.groceries = groceryRows;

  // Section 10 — Total ପରିମାଣ row = Total MDM meals served for that class.
  // Dry Ration distributions are excluded (Section 10 lists ingredients
  // actually cooked, so the meal-count row must match that scope).
  data.monthly.section10TotalMeals = {
    balBatika: mdmMeals.balVatika,
    primaryClass: mdmMeals.primary,
    upperPrimaryClass: mdmMeals.upperPrimary,
    upperSchool: mdmMeals.highSchool,
  };

  // English — Meals Availed (MDM+DR=Total formats for school days, mdm days, total meals)
  data.eng.mealsAvailed.enrolment.balVatika = String(toN(school?.balVatikaEnrollment) || "");
  data.eng.mealsAvailed.enrolment.primary = String(toN(school?.primaryEnrollment) || "");
  data.eng.mealsAvailed.enrolment.upperPrimary = String(toN(school?.upperPrimaryEnrollment) || "");
  data.eng.mealsAvailed.schoolDays.balVatika = workingDaysFmt;
  data.eng.mealsAvailed.schoolDays.primary = workingDaysFmt;
  data.eng.mealsAvailed.schoolDays.upperPrimary = workingDaysFmt;
  const mdmDaysOnly = String(mdmDaysSet.size || "");
  const mdmDrDaysFmt = fmt(mdmDaysSet.size, drDaysSet.size, 0, 0) || mdmDaysOnly;
  data.eng.mealsAvailed.mdmDaysServed.balVatika = mdmDrDaysFmt;
  data.eng.mealsAvailed.mdmDaysServed.primary = mdmDrDaysFmt;
  data.eng.mealsAvailed.mdmDaysServed.upperPrimary = mdmDrDaysFmt;
  data.eng.mealsAvailed.totalMealsServed.balVatika = fmt(mdmMeals.balVatika, drMeals.balVatika, 0, 0);
  data.eng.mealsAvailed.totalMealsServed.primary = fmt(mdmMeals.primary, drMeals.primary, 0, 0);
  data.eng.mealsAvailed.totalMealsServed.upperPrimary = fmt(mdmMeals.upperPrimary, drMeals.upperPrimary, 0, 0);

  // English — Fund Details (Expenditure during the month: MDM+DR=Total format)
  data.eng.fundDetails.cookingCostBalVatika.expenditureDuringMonth = fmt(mdmFundsByClass.balBatika, drFundsByClass.balBatika, 0, 2);
  data.eng.fundDetails.cookingCostPrimary.expenditureDuringMonth = fmt(mdmFundsByClass.primaryClass, drFundsByClass.primaryClass, 0, 2);
  data.eng.fundDetails.cookingCostUpperPrimary.expenditureDuringMonth = fmt(mdmFundsByClass.upperPrimaryClass, drFundsByClass.upperPrimaryClass, 0, 2);

  // English — Grain Details (Consumption during the month: MDM+DR=Total kg, 3-dec)
  data.eng.grainDetails.balVatikaRice.consumptionDuringMonth = fmt(mdmRiceByClass.balBatika, drRiceByClass.balBatika, 0, 3);
  data.eng.grainDetails.primaryRice.consumptionDuringMonth = fmt(mdmRiceByClass.primaryClass, drRiceByClass.primaryClass, 0, 3);
  data.eng.grainDetails.upperPrimaryRice.consumptionDuringMonth = fmt(mdmRiceByClass.upperPrimaryClass, drRiceByClass.upperPrimaryClass, 0, 3);

  // Cook payments — carry over from Quarterly Reports month entry if provided.
  // Otherwise, auto-fill the English Section 4 cook table from the CCH master
  // (English names + bank account / IFSC) so teachers don't have to retype them.
  if (monthlyCooks && monthlyCooks.length > 0) {
    data.eng.cookDetails = monthlyCooks.map((c, i) => ({
      slNo: String(i + 1),
      cookName: c.name || "",
      gender: c.gender || "",
      category: c.category || "",
      paymentMode: c.mode || "Bank",
      amountReceived: String(toN(c.amount) || ""),
    }));
    const totalCook = monthlyCooks.reduce((s, c) => s + toN(c.amount), 0);
    data.eng.fundDetails.cookCumHelper.expenditureDuringMonth = String(Number(totalCook.toFixed(2)) || "");
  } else if (Array.isArray(cchs) && cchs.length > 0) {
    data.eng.cookDetails = cchs.map((c, i) => {
      const hon = Number(c.monthlyHonorarium) || 0;
      return {
        slNo: String(i + 1),
        cookName: c.name || "",
        gender: c.gender || "",
        category: c.category || "",
        paymentMode: c.defaultMode || "Bank",
        amountReceived: hon > 0 ? hon.toFixed(2) : "",
      };
    });
    // NOTE: The Cook Cum Helper expenditure (English Section 3 → Fund Details) is
    // intentionally NOT auto-rolled from CCH honorarium. Honoraria are paid from a
    // separate state-level head, not from PM-POSHAN's monthly cooking-cost grant. The
    // cell stays blank/manual so HMs explicitly enter the actual disbursement.
  }

  // Fund Balance integration (auto-fetch classwise opening / received / closing into Odia + English MDM bills)
  // If the current month has no fund record but the previous month does, synthesise a
  // virtual fund record so cascade still flows into the bill (received = 0, opening = prev closing).
  let effectiveFundBalance = fundBalance;
  if (!effectiveFundBalance && prevFundBalance) {
    const poF = prevFundBalance.openingFund || {};
    const prF = prevFundBalance.receivedFund || {};
    const poR = prevFundBalance.openingRice || {};
    const prR = prevFundBalance.receivedRice || {};
    const pCost = (prevConsumed && prevConsumed.cost) || { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 };
    const pRice = (prevConsumed && prevConsumed.rice) || { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 };
    const empty = { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 };
    const opF = { ...empty }; const opR = { ...empty };
    ["balVatika", "primary", "upperPrimary", "highSchool"].forEach((k) => {
      opF[k] = (Number(poF[k]) || 0) + (Number(prF[k]) || 0) - (pCost[k] || 0);
      opR[k] = (Number(poR[k]) || 0) + (Number(prR[k]) || 0) - (pRice[k] || 0);
    });
    effectiveFundBalance = {
      openingFund: opF, receivedFund: { ...empty },
      openingRice: opR, receivedRice: { ...empty },
    };
  }
  if (effectiveFundBalance) {
    const oF = { ...(effectiveFundBalance.openingFund || {}) };
    const rF = effectiveFundBalance.receivedFund || {};
    const oR = { ...(effectiveFundBalance.openingRice || {}) };
    const rR = effectiveFundBalance.receivedRice || {};
    const num = (v) => Number(v) || 0;

    // ----- CASCADE: override stored opening with previous month's CLOSING -----
    // If the previous month had a fund record, the opening of THIS month must equal
    // (prev_opening + prev_received - prev_consumed) per class. This guarantees that
    // editing any past month's received/consumed automatically flows forward into
    // every subsequent monthly bill. (Skip if we already synthesised effectiveFundBalance
    // from prev — opening is already cascaded above.)
    if (prevFundBalance && fundBalance) {
      const poF = prevFundBalance.openingFund || {};
      const prF = prevFundBalance.receivedFund || {};
      const poR = prevFundBalance.openingRice || {};
      const prR = prevFundBalance.receivedRice || {};
      const pCost = (prevConsumed && prevConsumed.cost) || { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 };
      const pRice = (prevConsumed && prevConsumed.rice) || { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 };
      ["balVatika", "primary", "upperPrimary", "highSchool"].forEach((k) => {
        oF[k] = num(poF[k]) + num(prF[k]) - num(pCost[k]);
        oR[k] = num(poR[k]) + num(prR[k]) - num(pRice[k]);
      });
    }

    // Map FundBalance class keys → MDCF section row keys
    // FundBalance: balVatika / primary / upperPrimary / highSchool
    // Odia Section 6/7 rows: balBatika / primaryClass / upperPrimaryClass / upperSchool
    const ROW_MAP = [
      { fb: "balVatika",   row: "balBatika",          cookFundKey: "cookingCostBalVatika",     grainKey: "balVatikaRice",    expense: fundsByClass.balBatika,          riceExpense: riceByClass.balBatika },
      { fb: "primary",     row: "primaryClass",       cookFundKey: "cookingCostPrimary",       grainKey: "primaryRice",      expense: fundsByClass.primaryClass,       riceExpense: riceByClass.primaryClass },
      { fb: "upperPrimary",row: "upperPrimaryClass",  cookFundKey: "cookingCostUpperPrimary",  grainKey: "upperPrimaryRice", expense: fundsByClass.upperPrimaryClass,  riceExpense: riceByClass.upperPrimaryClass },
      { fb: "highSchool",  row: "upperSchool",        cookFundKey: null,                       grainKey: null,               expense: fundsByClass.upperSchool,        riceExpense: riceByClass.upperSchool },
    ];

    ROW_MAP.forEach(({ fb, row, cookFundKey, grainKey, expense, riceExpense }) => {
      const oFC = num(oF[fb]);
      const rFC = num(rF[fb]);
      const oRC = num(oR[fb]);
      const rRC = num(rR[fb]);
      const closingF = oFC + rFC - num(expense);
      const closingR = oRC + rRC - num(riceExpense);

      // Odia ୬. ଆର୍ଥିକ ବିବରଣୀ — populate per class row
      data.monthly.section6[row].previousBalance = oFC.toFixed(2);
      data.monthly.section6[row].received = rFC.toFixed(2);
      data.monthly.section6[row].remaining = closingF.toFixed(2);
      // Odia ୭. ଚାଉଳର ବିବରଣୀ — same per class
      data.monthly.section7[row].previousBalance = oRC.toFixed(3);
      data.monthly.section7[row].received = rRC.toFixed(3);
      data.monthly.section7[row].remaining = closingR.toFixed(3);

      // English Fund Details — only the 3 Cooking-Cost rows (Bal Vatika / Primary / Upper Primary)
      if (cookFundKey && data.eng.fundDetails[cookFundKey]) {
        data.eng.fundDetails[cookFundKey].openingBalance = oFC.toFixed(2);
        data.eng.fundDetails[cookFundKey].receivedDuringMonth = rFC.toFixed(2);
        data.eng.fundDetails[cookFundKey].closingBalance = closingF.toFixed(2);
      }
      // English Food Grains — only 3 Rice rows
      if (grainKey && data.eng.grainDetails[grainKey]) {
        data.eng.grainDetails[grainKey].openingBalance = oRC.toFixed(3);
        data.eng.grainDetails[grainKey].receivedDuringMonth = rRC.toFixed(3);
        data.eng.grainDetails[grainKey].closingBalance = closingR.toFixed(3);
      }
    });
  }

  return data;
}
